/**
 * 3D mechanical-equipment markers for the Viewer: one box glyph per placed
 * element (RTU / AHU / pump / VAV terminal), sized and colored per kind. A
 * THIN render of the mechanical engine's Output — no placement or sizing logic
 * lives here.
 *
 * Structure mirrors {@link ConduitLayer}: an InstancedMesh per kind
 * (`mechanical:<kind>`) so a whole storey renders in a handful of draw calls;
 * owns a THREE.Group the Viewer adds to its scene; shared geometry/material
 * caches are reused across rebuilds and disposed only in `dispose()`. The glyph
 * treatment mirrors {@link DuctLayer}'s equipment path (box footprint per kind,
 * translucent when screening-only).
 *
 * Coordinates: equipment arrives in plan meters (x east, y north, z up);
 * three.js is y-up, so instances map through planToThree (x, z, y) — the same
 * mapping DuctLayer/ConduitLayer use. The per-model alignment offset is applied
 * on `group` by the Viewer (see align.ts).
 *
 * NOTE on z: unlike ducts/devices (which are fed from normalized row endpoints
 * where the server already lifted z to world), mechanical equipment is read
 * from the raw `/output` payload where z is storey-LOCAL. The caller lifts it
 * by the storey elevation before handing rows here — exactly how the conduit
 * path in Viewer.tsx does it. This layer treats `z_m` as world z.
 */
import * as THREE from "three";
import type { MechanicalKind } from "../api/types";
import { planToThree } from "../mep/ducts";

/** What the layer needs per equipment row (subset of the Output element). */
export interface LayerMechanicalEquipment {
  id: string;
  kind: MechanicalKind;
  x_m: number;
  y_m: number;
  /** World z (storey elevation already applied by the caller). */
  z_m: number;
  /** True for estimating-grade rows — rendered translucent, like DuctLayer's AHU. */
  screening_only: boolean;
  /** Server-side storey id, for storey isolation. */
  storey_id: string | null;
}

/**
 * Glyph footprint per kind (plan w × d × vertical h, meters), matching the
 * `mechanical-equipment.yaml` catalog footprints: RTU 6×8 ft ≈ 1.8×2.4 m,
 * AHU 5×10 ft ≈ 1.5×3.0 m, pump 2×3 ft ≈ 0.6×0.9 m. The VAV terminal has no
 * catalog footprint (it is an in-duct box), so it gets a small marker cube.
 */
const MECHANICAL_SIZE: Record<MechanicalKind, [number, number, number]> = {
  rooftop_unit: [1.8, 2.4, 1.2],
  air_handling_unit: [1.5, 3.0, 1.8],
  pump: [0.6, 0.9, 0.7],
  vav_terminal: [0.5, 0.5, 0.3],
};

/**
 * Glyph fill color per kind — kept clear of the supply-blue / return-red duct
 * colors in DUCT_COLORS and of DuctLayer's amber AHU, so a combined
 * HVAC + Mechanical overlay stays readable.
 */
const MECHANICAL_COLOR: Record<MechanicalKind, number> = {
  rooftop_unit: 0x8b5cf6, // violet — roof plant
  air_handling_unit: 0x06b6d4, // cyan — indoor air plant
  pump: 0x10b981, // emerald — hydronic
  vav_terminal: 0xa3e635, // lime — zone terminal
};

export class MechanicalLayer {
  /** Added to the Viewer's scene; everything the layer renders lives here. */
  readonly group = new THREE.Group();

  private equipment: LayerMechanicalEquipment[] = [];
  private meshes = new Map<MechanicalKind, THREE.InstancedMesh>();
  private hiddenKinds = new Set<MechanicalKind>();
  private storeyFilter: string | null = null;

  // Shared geometry/material per kind, reused across rebuilds (kind toggles /
  // storey filter). Created lazily; disposed only in dispose(), never in
  // clearMeshes() — the glyph primitives depend only on the kind.
  private geoCache = new Map<string, THREE.BufferGeometry>();
  private matCache = new Map<string, THREE.MeshStandardMaterial>();

  constructor() {
    this.group.name = "istari-mechanical-layer";
  }

  /** Replace all equipment (called after generation/regeneration). */
  setEquipment(equipment: readonly LayerMechanicalEquipment[]): void {
    this.equipment = [...equipment];
    this.rebuild();
  }

  /** Toggle the whole mechanical layer (independent of ducts/devices/raceways). */
  setLayerVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  get layerVisible(): boolean {
    return this.group.visible;
  }

  /** Toggle one equipment kind (layer-panel checkboxes), like DeviceLayer. */
  setKindVisible(kind: MechanicalKind, visible: boolean): void {
    if (visible) {
      this.hiddenKinds.delete(kind);
    } else {
      this.hiddenKinds.add(kind);
    }
    this.rebuild();
  }

  /** Restrict equipment to one storey (`null` shows all). */
  setStoreyFilter(storeyId: string | null): void {
    this.storeyFilter = storeyId;
    this.rebuild();
  }

  /** Equipment kinds present, sorted (deterministic for UI toggles). */
  kinds(): MechanicalKind[] {
    return [...new Set(this.equipment.map((e) => e.kind))].sort();
  }

  /** Names of the meshes currently built, sorted (deterministic; tests/UI). */
  meshNames(): string[] {
    return [...this.meshes.values()].map((m) => m.name).sort();
  }

  /** Currently rendered instance count for one kind (visible markers). */
  visibleCount(kind: MechanicalKind): number {
    return this.meshes.get(kind)?.count ?? 0;
  }

  /** Total rendered marker instances across all kinds. */
  totalVisibleCount(): number {
    let total = 0;
    for (const mesh of this.meshes.values()) total += mesh.count;
    return total;
  }

  private cachedGeometry(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
    let geo = this.geoCache.get(key);
    if (!geo) {
      geo = make();
      this.geoCache.set(key, geo);
    }
    return geo;
  }

  private cachedMaterial(
    key: string,
    make: () => THREE.MeshStandardMaterial,
  ): THREE.MeshStandardMaterial {
    let mat = this.matCache.get(key);
    if (!mat) {
      mat = make();
      this.matCache.set(key, mat);
    }
    return mat;
  }

  private visibleEquipment(): Map<MechanicalKind, LayerMechanicalEquipment[]> {
    const byKind = new Map<MechanicalKind, LayerMechanicalEquipment[]>();
    for (const eq of this.equipment) {
      if (this.hiddenKinds.has(eq.kind)) continue;
      if (this.storeyFilter !== null && eq.storey_id !== this.storeyFilter) continue;
      const list = byKind.get(eq.kind) ?? [];
      list.push(eq);
      byKind.set(eq.kind, list);
    }
    return byKind;
  }

  private rebuild(): void {
    this.clearMeshes();
    const byKind = this.visibleEquipment();
    const matrix = new THREE.Matrix4();
    // Sorted kinds → deterministic scene-graph order.
    for (const kind of [...byKind.keys()].sort()) {
      const list = byKind.get(kind)!;
      const [w, d, h] = MECHANICAL_SIZE[kind] ?? [0.5, 0.5, 0.5];
      // Plan footprint is w × d with h vertical; three.js is y-up, so a plan
      // box (w × d × h) becomes a three box (w × h × d) — same as DuctLayer.
      const geometry = this.cachedGeometry(
        `mechanical:${kind}`,
        () => new THREE.BoxGeometry(w, h, d),
      );
      // Every mechanical capacity is estimating-grade today, so this is
      // effectively always true; keyed into the material cache anyway so a
      // future manufacturer-tier selection renders solid without a code change.
      const screeningOnly = list.every((e) => e.screening_only);
      const mesh = new THREE.InstancedMesh(
        geometry,
        this.cachedMaterial(
          `mechanical:${kind}:${screeningOnly}`,
          () =>
            new THREE.MeshStandardMaterial({
              color: new THREE.Color(MECHANICAL_COLOR[kind] ?? 0xffffff),
              transparent: screeningOnly,
              opacity: screeningOnly ? 0.6 : 1.0,
              metalness: 0.2,
              roughness: 0.7,
            }),
        ),
        list.length,
      );
      mesh.name = `mechanical:${kind}`;
      list.forEach((eq, i) => {
        matrix.makeTranslation(...planToThree([eq.x_m, eq.y_m, eq.z_m]));
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.meshes.set(kind, mesh);
      this.group.add(mesh);
    }
  }

  private clearMeshes(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh);
      // Geometry/material are shared caches reused across rebuilds, so they
      // are NOT disposed here — only in dispose(). This frees the
      // InstancedMesh's own instance buffers.
      mesh.dispose();
    }
    this.meshes.clear();
  }

  dispose(): void {
    this.clearMeshes();
    for (const geo of this.geoCache.values()) geo.dispose();
    for (const mat of this.matCache.values()) mat.dispose();
    this.geoCache.clear();
    this.matCache.clear();
    this.equipment = [];
  }
}
